import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, ShoppingBag, X } from "lucide-react";
import { useCart } from "../context/CartContext";

const ANCHORS = [
  { id: "why", label: "Why LED" },
  { id: "spectrum", label: "Spectrum" },
  { id: "savings", label: "Savings" },
];

export default function Nav() {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goAnchor = (item) => {
    setMobileOpen(false);
    const id = item.id;
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 250);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-[#39FF14]/15" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5" onClick={() => setMobileOpen(false)}>
          <img src="/img/brand/neon-sign.jpeg" alt="SuperTechLED" className="h-8 w-auto mix-blend-screen" />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <Link
            to="/shop"
            data-testid="nav-shop-link"
            className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#C9DCD0] transition-colors hover:text-[#39FF14]"
          >
            The Range
          </Link>
          {ANCHORS.map((a) => (
            <a
              key={a.label}
              href={`/#${a.id}`}
              data-testid={`nav-${a.id}-link`}
              onClick={(e) => {
                e.preventDefault();
                goAnchor(a);
              }}
              className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#C9DCD0] transition-colors hover:text-[#39FF14]"
            >
              {a.label}
            </a>
          ))}
          <Link
            to="/guides"
            data-testid="nav-guides-link"
            className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#C9DCD0] transition-colors hover:text-[#39FF14]"
          >
            Guides
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <span className="hidden rounded-full border border-[#39FF14]/20 px-3 py-1 font-mono2 text-[10px] tracking-widest text-[#86EFAC] sm:block">
            AU · AUD
          </span>
          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className="relative flex h-10 items-center gap-2 rounded-full border border-[#39FF14]/30 bg-[#0A150F]/70 px-4 text-[#F0FDF4] transition-all hover:border-[#39FF14]/60 hover:shadow-[0_0_20px_rgba(57,255,20,0.25)]"
          >
            <ShoppingBag className="h-4 w-4 text-[#39FF14]" />
            <span className="font-mono2 text-[11px] uppercase tracking-widest">Cart</span>
            <AnimatePresence mode="popLayout">
              <motion.span
                key={count}
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 22 }}
                data-testid="nav-cart-count"
                suppressHydrationWarning
                className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#39FF14] px-1 font-mono2 text-[10px] font-bold text-[#050B07]"
              >
                {count}
              </motion.span>
            </AnimatePresence>
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#39FF14]/30 text-[#F0FDF4] md:hidden"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="glass overflow-hidden border-t border-[#39FF14]/10 md:hidden"
          >
            <div className="flex flex-col gap-4 px-6 py-5">
              <Link to="/shop" onClick={() => setMobileOpen(false)} className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#C9DCD0]">
                The Range
              </Link>
              {ANCHORS.map((a) => (
                <a
                  key={a.label}
                  href={`/#${a.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    goAnchor(a);
                  }}
                  className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#C9DCD0]"
                >
                  {a.label}
                </a>
              ))}
              <Link to="/guides" onClick={() => setMobileOpen(false)} className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#C9DCD0]">
                Guides
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
